"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  clearError,
  login,
  register as registerUser,
  selectAuthError,
} from "@/store/slices/authSlice";
import {
  CircleAlert,
  Eye,
  EyeOff,
  Loader2,
  Square,
  SquareCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

interface SignInFormData {
  email: string;
  password: string;
}

export default function AuthForm({
  onForgotPasswordClick,
}: {
  onForgotPasswordClick: () => void;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");

  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<"signin" | "signup">(
    type === "register" ? "signup" : "signin"
  );

  // Track if form has been interacted with
  const [formInteracted, setFormInteracted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
      name: "",
      agreeToTerms: false,
    },
  });

  const error = useSelector(selectAuthError);

  // Reset form state when tab changes
  useEffect(() => {
    // Reset form errors and fields
    reset({
      email: "",
      password: "",
      name: "",
      agreeToTerms: false,
    });
    // Clear any auth errors
    dispatch(clearError());
    // Reset form interaction state
    setFormInteracted(false);
  }, [activeTab, dispatch, reset]);

  const onSubmit = async (data: SignInFormData) => {
    try {
      if (activeTab === "signin") {
        // For signin, use login action
        const resultAction = await dispatch(login(data) as any);

        // Check if the action was fulfilled or rejected
        if (login.fulfilled.match(resultAction)) {
          console.log("Login successful, navigating to dashboard");

          // const roleCode = resultAction.payload.role.code;

          // Temporarily redirect all users to dashboard until my-courses module is ready
          // Once my-courses module is ready, use the role-based navigation
          // Check if user is a student (using ROLES.STUDENT for future reference)
          // const isStudent = roleCode === ROLES.STUDENT;
          // const navigatePath = "/dashboard"; // For now, always redirect to dashboard

          // navigate(navigatePath);
        } else if (login.rejected.match(resultAction)) {
          // This will catch the rejection from createAsyncThunk
          console.error("Login failed with error:", resultAction.error);
          // The toast is already handled in the authSlice, but we can add additional handling here
        }
      } else {
        // For signup, handle registration
        console.log("Registration data:", data);

        // Pass email and token for registration
        const resultAction = await dispatch(
          registerUser({ email: data.email }) as any
        );

        // Check if the action was fulfilled
        if (registerUser.fulfilled.match(resultAction)) {
          const registrationData = resultAction.payload;
          console.log("Registration successful:", registrationData);

          // Navigate to OTP verification page with email
          navigate(
            `/auth/otp?email=${encodeURIComponent(data.email)}&token=${
              registrationData?.id as string
            }`
          );
        } else if (registerUser.rejected.match(resultAction)) {
          console.error("Registration failed with error:", resultAction.error);
        }
      }
    } catch (error) {
      // This catch block will only execute if there's an error in the code above,
      // not for login failures which are handled by the thunk
      console.error("Unexpected error in onSubmit:", error);
      // errorToast("An unexpected error occurred", "Please try again later.");
    }
  };

  return (
    <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl border border-white/20 backdrop-blur-sm">
      <div className="space-y-6">
        <div>
          <h3 className="text-xl text-center font-bold text-textColor mb-2">
            Welcome!
          </h3>
          <p className="text-textColor text-center px-4 text-sm">
            {activeTab === "signin"
              ? "A platform for music education—accessible, engaging, and truly effective."
              : "A platform for music education—accessible, engaging, and truly effective."}
          </p>
        </div>

        <div className="flex bg-gray-100 rounded-full p-2">
          <button
            onClick={() => setActiveTab("signin")}
            className={`flex-1 py-3 px-4 rounded-full text-[16px] font-bold transition-all duration-200 ${
              activeTab === "signin"
                ? "bg-[#C6C2FF] text-textColor shadow-md"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Sign In
          </button>
          <button
            onClick={() => setActiveTab("signup")}
            className={`flex-1 py-3 px-4 rounded-full text-[16px] font-bold transition-all duration-200 ${
              activeTab === "signup"
                ? "bg-[#C6C2FF] text-textColor shadow-md"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label
              htmlFor="email"
              className="block text-md font-medium text-gray-700 mb-2"
            >
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Your email address"
              className={`w-full h-12 rounded-xl 
                 ${
                   errors.email
                     ? "border-[#FF3B30]"
                     : formInteracted
                     ? "border-gray-200 focus:outline-none focus:border-[#7872C8] focus:ring-[#7872C8]"
                     : "border-gray-200"
                 }
                `}
              onFocus={() => setFormInteracted(true)}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Please enter a valid email address",
                },
              })}
            />
            {errors.email && (
              <span className="text-[#FF3B30] text-[12px] font-[500] mt-1 block">
                {errors.email.message}
              </span>
            )}
          </div>

          {activeTab === "signin" && (
            <div>
              <label
                htmlFor="password"
                className="block text-md font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Your password"
                  className={`w-full h-12 rounded-xl pr-12
                ${
                  errors.password
                    ? "border-[#FF3B30]"
                    : formInteracted
                    ? "border-gray-200 focus:outline-none focus:border-[#7872C8] focus:ring-[#7872C8]"
                    : "border-gray-200"
                }`}
                  onFocus={() => setFormInteracted(true)}
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                  })}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="text-[#FF3B30] text-[12px] font-[500] mt-1 block">
                  {errors.password.message}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => {
                  setValue("agreeToTerms", !watch("agreeToTerms"));
                  trigger("agreeToTerms");
                }}
              >
                {watch("agreeToTerms") ? (
                  <SquareCheck className="w-6 h-6 text-[#5C88C5]" />
                ) : (
                  <Square className="w-6 h-6 text-[#354052]" />
                )}
              </div>

              <label
                htmlFor="terms"
                className="text-xs font-bold text-gray-700"
              >
                I agree to{" "}
                <a
                  href="https://musictemple.in/terms-of-use/"
                  target="_blank"
                  className="text-[#7872C8] hover:underline"
                >
                  Terms & Condition
                </a>
              </label>
            </div>

            {activeTab === "signin" && (
              <button
                type="button"
                onClick={onForgotPasswordClick}
                className="text-xs text-gray-700 hover:underline font-bold"
              >
                Forgot password?
              </button>
            )}
          </div>

          {error && (
            <div className="w-full bg-[#FFE9EB] p-2 rounded-lg">
              <div className="w-full h-full border-l-4 pl-3 border-[#FF152C] flex flex-row items-center justify-start gap-3 rounded-lg">
                <div className="w-[24px] h-[24px]">
                  <CircleAlert className="w-[24px] h-[24px] text-[#FF152C]" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {error}
                </span>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full mt-4 h-12 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-semibold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            disabled={
              isSubmitting ||
              !isValid ||
              !watch("agreeToTerms") ||
              !watch("email") ||
              (activeTab === "signin" && !watch("password")) ||
              Object.keys(errors).length > 0
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {activeTab === "signin"
                  ? "Signing In..."
                  : "Creating Account..."}
              </>
            ) : activeTab === "signin" ? (
              "SIGN IN"
            ) : (
              "Continue"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
